// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useTransferInstructions } from '@hooks/queries/registry/transferInstruction/useTransferInstructions';
import React, { useState } from 'react';
import { Box } from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { TransferInstruction } from '@daml.js/splice-api-token-transfer-instruction/lib/Splice/Api/Token/TransferInstructionV1';
import { ActionNeededIndicator } from '../../../components/ActionNeededIndicator';
import { ChoiceDialog } from '../../../components/ChoiceDialog';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { StateCell, Status } from '../../../components/StateCell';
import { DataTable } from '../../../components/Table/DataTable';
import { StyledHeader } from '../../../components/Table/Header';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import useAcceptedTransfers from '../../../hooks/queries/registry/transfer/useAcceptedTransfers';
import useExecutedTransfers from '../../../hooks/queries/registry/transfer/useExecutedTransfers';
import useFailedTransfers from '../../../hooks/queries/registry/transfer/useFailedTransfers';
import useRejectedTransfers from '../../../hooks/queries/registry/transfer/useRejectedTransfers';
import useTransferOffers from '../../../hooks/queries/registry/transfer/useTransferOffers';
import useTransferRequests from '../../../hooks/queries/registry/transfer/useTransferRequests';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { fmt } from '../../../utils/common';
import { PageWrapper } from '../../PageWrapper';
import { Direction } from './Direction';
import { OfferTransferDialog } from './OfferTransferDialog';
import { TransferDetails } from './TransferDetails';
import { Row } from './types';
import { useCreateTransferActions } from './useCreateTransferActions';

const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'state',
      headerName: 'State',
      renderHeader: () => (
        <StyledHeader sx={{ ml: 2.5 }} variant="caption">
          State
        </StyledHeader>
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <ActionNeededIndicator
            isOffered={params.row.state === 'Offered' || params.row.state === 'Offered2step'}
            receiver={params.row.payload.receiver}
          />

          <StateCell reason={params.row.reason} state={params.row.state as Status} />
        </Box>
      ),
      flex: 80,
      filterable: false,
      minWidth: 100,
    },
    {
      field: 'sender',
      headerName: 'Sender',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Direction sender={params.row.payload.sender} receiver={params.row.payload.receiver} />
          <PartyDisplay party={params.row.payload.sender} />
        </Box>
      ),
      flex: 100,
      filterable: false,
      minWidth: 100,
    },
    {
      field: 'receiver',
      headerName: 'Receiver',
      renderCell: (params) => <PartyDisplay party={params.row.payload.receiver} />,
      flex: 100,
      filterable: false,
      minWidth: 100,
    },
    {
      field: 'instrument',
      headerName: 'Instrument',
      valueGetter: (params) => params.row.payload.instrumentIdentifier.id,
      flex: 150,
      filterable: false,
      minWidth: 100,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      renderCell: (params) => fmt(params.row.payload.amount, 2),
      valueGetter: (params) => Number(params.row.payload.amount),
      flex: 150,
      filterable: false,
      minWidth: 100,
    },
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 100,
      filterable: false,
      minWidth: 100,
    },
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 100,
      filterable: false,
      minWidth: 100,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      renderCell: (params) => <PartyDisplay party={params.row.payload.provider} />,
      flex: 100,
      filterable: false,
      minWidth: 100,
    },

    {
      field: 'reference',
      headerName: 'Reference',
      valueGetter: (params) => params.row.payload.reference,
      flex: 150,
      filterable: false,
      minWidth: 100,
    },
  ];
};

export const Transfers = () => {
  const registrarServices = useRegistrarServices();
  const holdings = useHoldings();
  const transferRequests = useTransferRequests();
  const transferOffers = useTransferOffers();
  const acceptedTransfers = useAcceptedTransfers();
  const rejectedTransfers = useRejectedTransfers();
  const executedTransfers = useExecutedTransfers();
  const failedTransfers = useFailedTransfers();
  const transferInstructions = useTransferInstructions();
  const [dialogName, setDialogName] = React.useState('');
  const onCloseDialog = () => {
    setDialogName('');
  };
  const [selectedRow, setSelectedRow] = useState<Row | undefined>(undefined);
  const onRowClick = (r: GridRowParams<Row>) => {
    setSelectedRow(r.row);
  };
  const onClose = () => {
    setSelectedRow(undefined);
  };
  const actions = useCreateTransferActions(onClose, selectedRow);

  const isLoading =
    registrarServices.isLoading ||
    holdings.isLoading ||
    transferRequests.isLoading ||
    transferOffers.isLoading ||
    acceptedTransfers.isLoading ||
    rejectedTransfers.isLoading ||
    executedTransfers.isLoading ||
    failedTransfers.isLoading ||
    !holdings.data ||
    !transferRequests.data ||
    !transferOffers.data ||
    !acceptedTransfers.data ||
    !rejectedTransfers.data ||
    !executedTransfers.data ||
    !failedTransfers.data ||
    transferInstructions.isLoading ||
    !transferInstructions.data;
  if (isLoading) return <Loading />;
  const rows = transferRequests.data
    .map((r) => ({
      contractId: r.contractId as string,
      state: 'Requested',
      payload: r.payload.transfer,
    }))
    .concat(
      transferOffers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Offered',
        payload: r.payload.transfer,
      })),
    )
    .concat(
      transferInstructions.data.map((r) => {
        const { transfer } = r.view(TransferInstruction);
        const payload = {
          sender: transfer.sender,
          receiver: transfer.receiver,
          instrumentIdentifier: {
            source: transfer.instrumentId.admin,
            id: transfer.instrumentId.id,
            scheme: 'RegistrarInternalScheme',
          },
          amount: transfer.amount,
          registrar: transfer.instrumentId.admin,
          reference: transfer.meta.values['splice.lfdecentralizedtrust.org/reason'],
        };
        return {
          contractId: r.contractId as string,
          state: `Offered2step`,
          payload,
        } as Row;
      }),
    )
    .concat(
      acceptedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Accepted',
        payload: r.payload.transfer,
      })),
    )
    .concat(
      rejectedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Rejected',
        payload: r.payload.transfer,
        reason: r.payload.reason,
      })),
    )
    .concat(
      executedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: 'Executed',
        payload: r.payload.transfer,
      })),
    )
    .concat(
      failedTransfers.data.map((r) => ({
        contractId: r.contractId as string,
        state: `Failed`,
        payload: r.payload.transfer,
        reason: r.payload.reason,
      })),
    );
  const buttons = [
    {
      name: 'offer transfer',
      onClick: () => setDialogName('transfer'),
    },
  ];
  return (
    <PageWrapper buttons={buttons} pageName="Transfers">
      <ChoiceDialog
        open={!!selectedRow}
        onClose={onClose}
        actions={actions}
        title="Transfer Details"
      >
        {selectedRow && <TransferDetails row={selectedRow} />}
      </ChoiceDialog>
      {dialogName === 'transfer' && <OfferTransferDialog open onClose={onCloseDialog} />}
      <DataTable
        columns={createHeaders()}
        rows={rows}
        onRowClick={onRowClick}
        rowKey={(a) => a.contractId}
      />
    </PageWrapper>
  );
};
